import { Donut, type DonutSlice } from "@/components/ui/Donut";
import { Icon } from "@/components/ui/Icon";
import { ErrorState } from "@/components/ui/States";
import { useWarehouseFill, type FillRow } from "@/lib/api/dashboard";
import { qty } from "@/lib/format";
import { STATUS_LABELS } from "@/lib/labels";
import "./dashboard.css";

/**
 * Ombor to'ldirilishi.
 *
 * IKKI XIL FOIZ bor va ular adashtirilmasligi kerak:
 *
 *   - TAQSIMOT — tovarning qaysi qismi shu kesimda. Maxraji: butun
 *     ombordagi miqdor. Dumaloq diagramma va zona ulushlari shu.
 *   - TO'LDIRILISH — kesim o'z sig'imining qancha qismini egallagan.
 *     Maxraji: o'sha kesimning pallet o'rinlari. ABC zonalari shu.
 *
 * Shuning uchun ular alohida bo'limlarda va sarlavhalari ham
 * boshqacha: bitta jadvalda yonma-yon tursa, ikkita foiz bir xil
 * narsa deb o'qilardi.
 *
 * Davrga bog'liq emas: bu HOZIRGI qoldiq holati, tarix emas.
 */

/**
 * Yacheyka vazifalari — ombor bo'ylab OQIM tartibida: tovar
 * qabuldan kirib, saqlashga tushadi, terishga chiqadi va
 * jo'natiladi. Alifbo tartibi bu yo'lni buzardi.
 *
 * Kodlar topology domenidan (`LocationFunction`).
 */
const FUNCTION_ORDER = [
  "RECEIPT",
  "BUFFER",
  "MX",
  "OX",
  "CONTROL",
  "SHIPPING",
  "YELLOW",
  "RED",
  "RETURN",
  "CUSTOMS",
  "DEFECT",
  "PRODUCTION",
];

const FUNCTION_COLORS: Record<string, string> = {
  RECEIPT: "var(--task-receipt)",
  BUFFER: "var(--task-transfer)",
  MX: "var(--task-putaway)",
  OX: "var(--task-pick)",
  CONTROL: "var(--task-control)",
  SHIPPING: "var(--task-shipment)",
  RETURN: "var(--task-return)",
  CUSTOMS: "var(--task-count)",
  PRODUCTION: "var(--task-production)",
};

const ZONE_COLORS = [
  "var(--task-receipt)",
  "var(--task-putaway)",
  "var(--task-pick)",
  "var(--task-shipment)",
  "var(--task-transfer)",
  "var(--task-production)",
];

const label = (value: string) =>
  STATUS_LABELS[value] ?? (value === "—" ? "Belgilanmagan" : value);

const number = (value: number) => value.toLocaleString("uz");

/** Ulush foizi. Maxraj nol bo'lsa foiz YO'Q — nol emas. */
function share(value: number, total: number): string {
  if (total <= 0) return "—";
  return `${((value / total) * 100).toFixed(1)}%`;
}

function sumQty(rows: FillRow[]): number {
  return rows.reduce((total, row) => total + Number(row.qty || 0), 0);
}

export function WarehouseFill() {
  const fill = useWarehouseFill();

  if (fill.error) {
    return <ErrorState error={fill.error} onRetry={() => void fill.refetch()} />;
  }
  if (fill.isLoading || !fill.data) {
    return <div className="skeleton" style={{ height: 260 }} />;
  }

  const functions = [...fill.data.functions].sort(
    (left, right) =>
      orderOf(left.key) - orderOf(right.key) || left.key.localeCompare(right.key),
  );
  const zones = [...fill.data.zones].sort(
    (left, right) => Number(right.qty) - Number(left.qty),
  );
  // Klassi berilmagan kesim OXIRIDA: alifbo bo'yicha «—» A dan
  // oldin turib, ro'yxat eng muhim bo'lmagan qatordan boshlanardi.
  const abcZones = [...fill.data.abc_zones].sort(
    (left, right) =>
      Number(left.key === "—") - Number(right.key === "—") ||
      left.key.localeCompare(right.key),
  );

  const totalQty = sumQty(functions);
  const totalLP = functions.reduce((total, row) => total + row.lp_count, 0);

  const slices: DonutSlice[] = functions
    .filter((row) => Number(row.qty) > 0)
    .map((row) => ({
      key: row.key,
      label: label(row.key),
      value: Number(row.qty),
      color: FUNCTION_COLORS[row.key] ?? "var(--task-other)",
    }));

  return (
    <div className="fill-layout">
      <div className="fill-donut">
        <Donut
          slices={slices}
          total={totalQty}
          centerValue={number(totalQty)}
          centerLabel="dona, jami"
        />
        <p className="field-hint" style={{ textAlign: "center", margin: 0 }}>
          {number(totalLP)} pallet (LP)
        </p>
      </div>

      <div className="fill-tables">
        <section>
          <h3 className="fill-title">
            <Icon name="warehouse" size={14} />
            Hudud bo'yicha taqsimot
          </h3>
          <p className="fill-note">
            Ombordagi tovarning qaysi qismi qayerda turganini ko'rsatadi.
          </p>
          <FillBars rows={functions} total={totalQty} colorOf={(row) =>
            FUNCTION_COLORS[row.key] ?? "var(--task-other)"} />
        </section>

        <section>
          <h3 className="fill-title">
            <Icon name="topology" size={14} />
            Korpus bo'yicha taqsimot
          </h3>
          <p className="fill-note">
            Zona kodi — korpus (A, B, C) va uning vazifasi.
          </p>
          <FillBars
            rows={zones}
            total={totalQty}
            colorOf={(_, index) => ZONE_COLORS[index % ZONE_COLORS.length]!}
          />
        </section>

        <section>
          <h3 className="fill-title">
            <Icon name="layers" size={14} />
            ABC zonalari — to'ldirilish
          </h3>
          {/* Bu yerdagi foiz TAQSIMOT emas: har bir zona O'Z
              sig'imining qancha qismini egallaganini ko'rsatadi.
              Shuning uchun yig'indisi 100% bo'lishi shart emas. */}
          <p className="fill-note">
            Har bir zona o'z pallet sig'imining qancha qismini egallagan.
            Yig'indisi 100% bo'lishi shart emas.
          </p>
          <FillLoad rows={abcZones} />
        </section>
      </div>
    </div>
  );
}

function orderOf(key: string): number {
  const index = FUNCTION_ORDER.indexOf(key);
  return index === -1 ? FUNCTION_ORDER.length : index;
}

/** Taqsimot: ulush butun ombordan. */
function FillBars({
  rows,
  total,
  colorOf,
}: {
  rows: FillRow[];
  total: number;
  colorOf: (row: FillRow, index: number) => string;
}) {
  if (rows.length === 0) {
    return <p className="field-hint">Ma'lumot yo'q — yacheyka reyestri bo'sh.</p>;
  }

  return (
    <ul className="fill-list">
      {rows.map((row, index) => {
        const value = Number(row.qty || 0);
        const percent = total > 0 ? (value / total) * 100 : 0;

        return (
          <li className="fill-row" key={`${row.key}-${row.warehouse_code ?? ""}`}>
            <span className="fill-name" title={row.key}>
              {label(row.key)}
            </span>
            <span className="fill-track" aria-hidden="true">
              <span
                className="fill-bar"
                style={{ width: `${percent}%`, background: colorOf(row, index) }}
              />
            </span>
            <span className="fill-qty num">{qty(row.qty)}</span>
            <span className="fill-percent num">{share(value, total)}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** To'ldirilish: ulush kesimning O'Z sig'imidan. */
function FillLoad({ rows }: { rows: FillRow[] }) {
  if (rows.length === 0) {
    return <p className="field-hint">Yacheykalarga ABC zonasi berilmagan.</p>;
  }

  return (
    <ul className="fill-list">
      {rows.map((row) => {
        // Sig'imi ko'rsatilmagan yacheyka uchun foiz YO'Q: nolga
        // bo'lish o'rniga buni ochiq aytamiz — reyestrda sig'im
        // to'ldirilmagani ham ma'lumot.
        const hasCapacity = row.capacity_slots > 0;
        const percent = hasCapacity ? (row.lp_count / row.capacity_slots) * 100 : 0;
        const full = percent >= 90;

        return (
          <li className="fill-row" key={row.key}>
            <span className="fill-name">{label(row.key)}</span>
            <span className="fill-track" aria-hidden="true">
              <span
                className={`fill-bar${full ? " full" : ""}`}
                style={{ width: `${Math.min(percent, 100)}%` }}
              />
            </span>
            <span className="fill-qty num">
              {hasCapacity ? `${row.lp_count} / ${row.capacity_slots}` : "—"}
            </span>
            <span className="fill-percent num">
              {hasCapacity ? `${percent.toFixed(0)}%` : "sig'imsiz"}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
